import { QueryClient, RefetchOptions, RefetchQueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeRefetchQueryFilters } from "../../hooks";
import { buildTellescopeQueryKey } from "../../keys";

/**
 * Arguments for refetching queries in cache.
 *
 * @template Model - The Tellescope model name
 * @template Method - The query method for the model
 * @template TError - The error type for the queries
 */
export interface RefetchTellescopeQueriesArgs<
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
> {
  /** The Tellescope model name */
  model: Model;
  /** Query method name */
  method: Method;
  /** Optional request parameters to narrow down queries. If omitted, refetches all queries for the method. */
  request?: Partial<ParamsForMethod<Model, Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** Optional query filters to narrow down which queries to refetch */
  filters?: Omit<
    TellescopeRefetchQueryFilters<
      QueryDataForMethod<Model, Method>,
      TError,
      QueryDataForMethod<Model, Method>,
      TellescopeQueryKey<Model, Method>
    >,
    "queryKey"
  >;
  /** Optional refetch options (e.g., throwOnError, cancelRefetch) */
  options?: RefetchOptions;
}

/**
 * Refetches queries in the query client cache for matching Tellescope queries.
 * Refetched queries will be re-executed and their data updated.
 *
 * @param args - The refetch queries arguments
 * @returns A promise that resolves when refetching is complete
 *
 * @example
 * ```tsx
 * // Refetch all queries for a method
 * await refetchTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 * });
 *
 * // Refetch specific query
 * await refetchTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   request: { id: "user-123" },
 * });
 *
 * // Refetch with filters
 * await refetchTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getSome",
 *   filters: {
 *     type: "active", // Only refetch active queries
 *   },
 * });
 *
 * // Refetch stale queries only
 * await refetchTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   filters: {
 *     stale: true, // Only refetch stale queries
 *   },
 * });
 *
 * // Refetch with custom options
 * await refetchTellescopeQueries({
 *   queryClient,
 *   model: "users",
 *   method: "getOne",
 *   options: {
 *     throwOnError: true, // Throw if refetch fails
 *     cancelRefetch: false, // Don't cancel ongoing refetch
 *   },
 * });
 * ```
 */
export const refetchTellescopeQueries = async <
  Model extends ModelName,
  Method extends QueryMethodForModel<Model>,
  TError = Error,
>({
  model,
  method,
  request,
  queryClient,
  filters,
  options,
}: RefetchTellescopeQueriesArgs<Model, Method, TError>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request });

  return queryClient.refetchQueries(
    {
      queryKey,
      ...filters,
    } as RefetchQueryFilters<TellescopeQueryKey<Model, Method>>,
    options
  );
};
